import * as React from "react"
import Footer from "./components/Footer"
import RemoteSite from "./components/site/RemoteSite"
import PageSpinner from "./components/PageSpinner"
import { isForeignHost, resolveSite, subdomainFromHost } from "./lib/site-content"

/**
 * Every page, with the footer underneath it — MTC-FCS-046.
 * ============================================================================
 * Used by gatsby-browser.js AND gatsby-ssr.js, which have to agree: a footer
 * that only exists in the browser bundle appears after hydration, and the page
 * visibly grows a footer a moment after it loads.
 *
 * ── WHY NOT A <Layout> IN EVERY PAGE ───────────────────────────────────────
 * Because "every page" then means "every page somebody remembered". There are
 * seventeen of them and the next one is always the one that gets missed.
 *
 * The generated contractor sites are NOT pages in this sense — they are
 * rendered inside the builder and exported as their own documents, so they
 * never pass through here and keep their own footer.
 */
/**
 * ── A CUSTOMER SUBDOMAIN SERVES THEIR WEBSITE, NOT OURS — FCS-057 ──────────
 * Every address points at this one build, so whose site this is can only be
 * decided here, from the hostname. On acme.freecontractorservices.com the
 * marketing page is replaced entirely by that customer's website — no header,
 * no footer of ours, and no route of ours reachable underneath it.
 *
 * Server-side this always renders the normal page: there is no hostname at
 * build time, and every subdomain is served the same file. The swap happens
 * on the first paint in the browser, which is why RemoteSite shows a loading
 * state rather than a flash of the wrong site.
 */
/* A real component, because it uses hooks — and `wrapPageElement` is not one.
   Gatsby CALLS that function while rendering rather than mounting it as an
   element, so a hook inside it is an invalid hook call and every page fails
   to build. The component below is mounted properly, so its state is its own. */
const PageShell = ({ element }) => {
  /* Decided AFTER mounting, never during the first render — FCS-057.
     The server has no hostname and renders the marketing page into the HTML;
     a browser that renders something else on its first pass is a hydration
     mismatch, and React throws the tree away and warns. So the first paint
     matches the HTML, and the swap happens in an effect on the next tick. */
  const [customer, setCustomer] = React.useState("")
  const [resolving, setResolving] = React.useState(false)

  React.useEffect(() => {
    const host = window.location.hostname
    const named = subdomainFromHost(host)
    if (named) {
      setCustomer(named)
      return undefined
    }
    // A customer's own domain (acme.com) is not in the address: ask whose it is (FCS-117).
    // Until the answer is in, show a spinner rather than our marketing page under their domain.
    if (!isForeignHost(host)) return undefined
    let alive = true
    setResolving(true)
    resolveSite(host).then(found => {
      if (!alive) return
      setCustomer(found)
      setResolving(false)
    })
    return () => {
      alive = false
    }
  }, [])

  if (customer) return <RemoteSite subdomain={customer} />
  if (resolving) return <PageSpinner label="Loading…" />

  /* ── THE FOOTER STAYS AT THE BOTTOM — FCS-115 ─────────────────────────────
     "Footer is not staying in the footer on smaller sized pages … minimum page height
     should always be at least 100vh minus the footer." Every page used its own
     `min-height: 70vh` to approximate that, which is right on one screen size and wrong
     on every other: on a tall window a short page ended with the footer floating
     mid-screen and a blank strip under it.

     So the rule lives here, once. The shell is a column at least one screen tall; the
     page's <main> takes whatever height is left, and the footer sits under it. A short
     page is exactly one screen tall (footer on the bottom edge); a long one is as long as
     it is. See .site-shell in global.css. */
  return (
    <div className="site-shell">
      {element}
      <Footer />
    </div>
  )
}

const wrapPageElement = ({ element }) => <PageShell element={element} />

export default wrapPageElement
